import { css, html, classMap, ref, createRef } from 'lit-all'
import { enableBodyScroll, disableBodyScroll } from 'body-scroll-lock'
import { HandleOverlay } from 'handle-overlay'

class NovaDetailsModal extends HandleOverlay {
  static properties = {
    breakpoint: {
      type: String,
    },
    label: {
      type: String,
    },
    selector: {
      type: String,
    },
    key: {
      type: String,
      attribute: 'key',
    },
    open: {
      type: Boolean,
      attribute: 'is-open',
      reflect: true,
    },
    forcedOpen: {
      attribute: 'open',
    },
    lockHeight: {
      type: Boolean,
      attribute: 'lock-height',
    },
    lockScroll: {
      type: Boolean,
      attribute: 'lock-scroll',
    },
    heightOffset: {
      type: Number,
      attribute: 'height-offset',
    },
    enableHover: {
      type: Boolean,
      attribute: 'enable-hover',
    },
    _height: {
      state: true,
    },
    overlay: {
      type: String,
    },
    _overlayAdded: {
      type: Boolean,
    },
    iconSwitch: {
      type: String,
    },
    ctx: {
      type: String,
    },
    widthType: {
      type: String,
      attribute: 'widthType',
    },
  }

  $container = createRef()
  $details = createRef()
  EXIT_TIMEOUT = 600

  constructor() {
    super()

    this.open = false
    this.lockHeight = false
    // this.heightOffset = 3
    this.breakpoint = '(min-width: 750px)'
    this._overlayAdded = false
    this.bodyListener = this.bodyListener.bind(this)
    this.navLeaveTimer = 100
    this.close = this.close.bind(this)
    this._closeOtherModalsclose = this._closeOtherModals.bind(this)
    this.ctx = this.getAttribute('ctx') || ''
    this.widthType = this.getAttribute('widthType') || ''
    this.detailHovered = false
  }

  connectedCallback() {
    super.connectedCallback()
    this.addEventListener('modalOpened', this._closeOtherModals)

    document.body.addEventListener('nova:modal:close', this.close)
    document.body.addEventListener('nova:search:open', function () {
      this.open = true
    })
  }

  requestUpdate(name, value) {
    // Allows parents to close open modals
    if (name === 'forcedOpen') {
      this.closeDetails(false, !this.forcedOpen)
    }
    return super.requestUpdate(name, value)
  }

  _closeOtherModals() {
    if (!this.getAttribute('key')) return

    const allModals = document.querySelectorAll('nova-details-modal')

    const otherEls = Array.from(allModals).filter(
      (item) => item.getAttribute('key') !== this.key
    )

    for (const item of otherEls) {
      item.close();
    } 
 }

  updated(changedProperties) {
    this.handleSearchOpenState(changedProperties)
  }

  handleSearchOpenState(changedProperties) {
    if (changedProperties.has('open')) {
      const searchForm = this.querySelector('nova-search-form')
      if (searchForm) {
        searchForm.setAttribute('openState', this.open ? 'opened' : 'closed')
        if (this.open) searchForm.shadowRoot?.querySelector('input').focus()
      }
    }
  }

  interceptEvent(event) {
    event._intercepted = true
  }

  toggle(event = false, value = undefined) {
    if (event?.type === 'keyup' && event?.code.toUpperCase() !== 'ENTER') {
      return true
    }

    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault()
    }
    
    this.open = typeof value === 'undefined' ? !this.open : value
    
    if (this.open) {  
      this.addBodyListener()  
    } else {  
      this.removeBodyListener()  
    }

    this.open && this.lockHeight && this.setHeight()
    this.lockScroll && this.toggleScroll()
    this.open && this.broadcast()

    if (this.overlay === 'true') {
      this.bodyOverlay()
    }

    if (this.iconSwitch === 'true') {
      this.toggleIconSwitch()
    }
  }

  clearExitTimer() {
    try {
      clearTimeout(this.navLeaveTimer)
      this.navLeaveTimer = null
    } catch (error) {
      console.error(error)
    }
  }

  setExitTimer() {
    try {
      this.navLeaveTimer = setTimeout(() => {
        this.closeDetails()
      }, this.EXIT_TIMEOUT)
    } catch (error) {
      console.log(error)
    }
  }

  openDetails(event = false, value = undefined) {
    if (!this.enableHover) return

    if (event?.type === "keyup" && event?.code.toUpperCase() !== "ENTER") {
      return
    }

    event?.preventDefault()
    this.open = typeof value === 'undefined' ? true : value
    this.clearExitTimer()
    this.addBodyListener()
    this.lockHeight && this.setHeight()
    this.lockScroll && this.toggleScroll()
    this.broadcast()

    if (this.overlay === 'true') {
      this.bodyOverlay()
    }

    if (this.iconSwitch === 'true') {
      this.toggleIconSwitch()
    }
  }

  closeDetails() {
    // WHY WERE WE DOING THIS
    // this.open = typeof value === 'undefined' ? false : value

    this.open = false;
    this.removeBodyListener()

    this.lockScroll && this.toggleScroll()

    if (this.overlay === 'true') {
      this.bodyOverlay()
    }

    if (this.iconSwitch === 'true') {
      this.toggleIconSwitch()
    }
  }

  // Not Used??
  // handleMenuItemLeave(event = false, value = undefined) {
  //   event?.preventDefault();

  //   this.clearExitTimer(); // Clear any existing timers

  //   this.navLeaveTimer = setTimeout(() => {
  //     if (!this.detailHovered) {
  //       this.closeDetails();
  //     }
  //   }, this.EXIT_TIMEOUT); // Uses EXIT_TIMEOUT (now 300-500ms)
  // }

  closeFromDetails(event) {
    const path = event.composedPath()
    if (
      path.some((element) => {
        return element.matches?.('[data-close]')
      })
    ) {
      this.closeDetails()
    }
  }

  toggleScroll() {
    if (this.open) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
    }
  }

  closeWithEscape(event) {
    if (event.code.toUpperCase() === 'ESCAPE') {
      this.closeDetails()
    }
  }

  close() {
    this.open && this.closeDetails()
    this.lockScroll && this.toggleScroll()
  }

  isMobile() {
    return !window.matchMedia(this.breakpoint).matches
  }

  addBodyListener() {
    this.isMobile() && disableBodyScroll(this.$container?.value)
    document.body.addEventListener('click', this.bodyListener)
  }

  removeBodyListener() {
    enableBodyScroll(this.$container?.value)
    document.body.removeEventListener('click', this.bodyListener)
  }

  bodyListener(event) {
    if (
      !event._intercepted &&
      (this.ctx !== 'filters' || window.innerWidth <= 768) &&
      !event.target.closest('.needsclick')
    ) {
      this.toggle()
    }
  }

setHeight() {
    const { top: parentTop = 0 } = this.parentNode.getBoundingClientRect() || {}
    const { top: detailsTop = 0 } =
      this.$details?.value.getBoundingClientRect() || {}

    this._height = this.isMobile()
      ? `calc(100vh - ${Math.floor(
          parentTop + detailsTop - this.heightOffset
        )}px)`
      : ''
  }

  broadcast() {
    this.dispatchEvent(
      new CustomEvent('modalOpened', {
        bubbles: true,
        composed: true,
        detail: {
          value: this,
        },
      })
    )
  }

  toggleIconSwitch() {
    const openIcon = this.querySelector('.nova-details-model--button-open')
    const closeIcon = this.querySelector('.nova-details-model--button-close')

    if (openIcon && closeIcon) {
      openIcon.classList.toggle('visually-hidden', this.open)
      openIcon.tabIndex = this.open ? -1 : 0
      closeIcon.focus()
      closeIcon.classList.toggle('visually-hidden', !this.open)
      closeIcon.tabIndex = this.open ? 0 : -1
      openIcon.focus()
    }
  }
  onWrapperLeave() {
    if (!this.enableHover) return;

    this.clearExitTimer(); // Clear any previous exit timer

    this.navLeaveTimer = setTimeout(() => {
      if (!this.detailHovered) {
        this.closeDetails();
      }
    }, this.EXIT_TIMEOUT); // Delayed closing
  }

  render() {
    return html`
      <details
        ?open=${this.open}
        @click=${this.interceptEvent}
        @mouseenter=${this.openDetails}
        @mouseleave=${this.onWrapperLeave}
        ${ref(this.$container)}
      >
        <summary
          aria-expanded="${this.open}"
          aria-haspopup="dialog"
          role="button"
          class=${classMap({ 'is-open': this.open })}
          @click=${!this.enableHover ? this.toggle : null}
          ${ref(this.$container)}
        >
          <slot
            class=${classMap({ 'is-open': this.open })}
            name="summary"
            tabindex="-1"
          ></slot>
        </summary>

        <div
          class="content ${this.widthType === 'desktopSearch'
            ? 'content__desktop-search'
            : ''}"
          role="dialog"
          aria-modal="true"
          aria-label=${this.label}
          ${ref(this.$details)}
        >
          <nova-focus-trap active=${this.open}>
            <slot
              name="details"
              @keyup=${this.closeWithEscape}
              @click=${this.closeFromDetails}
              @mouseenter=${() => {
                this.detailHovered = true
              }}
              @mouseleave=${() => {
                this.detailHovered = false
              }}
            ></slot>
          </nova-focus-trap>
        </div>
      </details>
    `
  }

  static styles = css`
    summary {
      list-style-type: none;
    }

    summary:focus {
      outline: 1px solid var(--nova-color-focus);
      outline-offset: var(--nova-focus-outline-offset);
    }

    summary::-webkit-details-marker {
      display: none;
    }

    .content {
      position: var(--nova-position, absolute);
      top: 125px;
      left: 10px;
      right: 10px;
      bottom: 20px;
      background: var(--nova-modal-background);
      z-index: var(--nova-z-index-inner, 1);
      border-radius: 12px;
    }

    ::slotted([slot='details']) {
      width: 100%;
      display: flex;
      flex-direction: column;
    }

    :host {
      z-index: var(--nova-z-index, 100);
    }
  `
}

customElements.define('nova-details-modal', NovaDetailsModal)
